import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * shadcn/ui sheet — an edge-anchored dialog.
 *
 * Built on `@radix-ui/react-dialog`, which is already a dependency: a Sheet IS a dialog
 * with a different transform, so this adds no package. AntD's `Drawer` is deliberately
 * not used here — CLAUDE.md §8 puts the SHELL on shadcn, and AntD's Drawer is a container
 * for data-dense content (see `OfficerDrawer`), not shell chrome.
 *
 * ⚠️ The content is UNMOUNTED while closed, and must stay that way. The mobile drawer
 * renders a second copy of the navigation; if it were `forceMount`ed, every
 * `getByRole('link', { name: 'Officers' })` in the suite would fail with "found multiple
 * elements" — a different error from the one people go looking for.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

const SIDE_CLASSES = {
  left: 'inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
  right:
    'inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
} as const;

export type SheetContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  side?: keyof typeof SIDE_CLASSES;
};

export const SheetContent = ({ className, children, side = 'right', ...props }: SheetContentProps) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 bg-overlay',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      )}
    />
    <DialogPrimitive.Content
      className={cn(
        'fixed z-50 flex flex-col border-border bg-card text-card-foreground transition ease-in-out',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200',
        SIDE_CLASSES[side],
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-3 top-3 rounded-md p-1 text-subtle transition-colors hover:bg-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <X className="size-4" aria-hidden />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
);

export const SheetTitle = ({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title className={cn('text-sm font-semibold', className)} {...props} />
);

export const SheetDescription = ({ className, ...props }: ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description className={cn('text-sm text-muted-foreground', className)} {...props} />
);
